import { View, Text, Heading, Button } from '@aws-amplify/ui-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <View style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative'
    }}>
      {/* Background gradient */}
      <View
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: -1
        }}
      >
        {/* Animated bubbles */}
        <View style={{ position: 'absolute', top: '-100px', left: '-100px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', animation: 'float 6s ease-in-out infinite' }} />
        <View style={{ position: 'absolute', bottom: '-150px', right: '-150px', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', animation: 'float 8s ease-in-out infinite reverse' }} />
        <View style={{ position: 'absolute', top: '40%', left: '10%', width: '250px', height: '250px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', animation: 'float 10s ease-in-out infinite' }} />
      </View>

      {/* Main Content */}
      <View style={{ 
        flex: 1, 
        padding: '2rem',
        maxWidth: '1400px',
        width: '100%',
        margin: '0 auto'
      }}>
        <View style={{
          background: 'rgba(30, 35, 45, 0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '3rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Heading level={1} style={{ 
            color: 'white', 
            marginBottom: '0.5rem',
            fontSize: '2.5rem',
            textAlign: 'center'
          }}>
            Privacy Policy
          </Heading>
          
          <Text style={{ 
            color: 'rgba(255,255,255,0.7)', 
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            Last updated: August 2025
          </Text>

          {/* Grid Layout */}
          <View style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem',
            color: 'white',
            lineHeight: '1.6'
          }}>
            {/* Section 1 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Information We Collect
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Account Details:</strong> Email, first name, last name
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>CV Documents:</strong> Uploaded PDFs (AES-256 encrypted)
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Analysis Data:</strong> AI feedback and compatibility scores
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                • <strong>Usage Logs:</strong> Service interactions for debugging
              </Text>
            </View>

            {/* Section 2 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Legal Basis (GDPR)
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Consent:</strong> Explicitly given at signup
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Contract:</strong> Necessary to provide services
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                • <strong>Legitimate Interests:</strong> Security and fraud prevention
              </Text>
            </View>

            {/* Section 3 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                How We Use Your Data
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • Provide CV analysis and improvement suggestions
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • Authenticate and secure your account
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • Display aggregated job listings
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                • Improve service quality and AI accuracy
              </Text>
            </View>

            {/* Section 4 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                AI Disclosure
              </Heading>
              <View style={{ 
                background: 'rgba(254, 215, 215, 0.1)', 
                border: '2px solid rgba(245, 101, 101, 0.5)',
                borderRadius: '10px',
                padding: '1rem'
              }}>
                <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                  All CV feedback is generated by <strong>OpenAI GPT-4o</strong>. 
                  This is AI-generated content, not human career advice. 
                  Your CV content is sent to OpenAI for processing.
                </Text>
              </View>
            </View>

            {/* Section 5 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Data Security
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Encryption:</strong> AES-256 for stored files
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Transmission:</strong> TLS/HTTPS protocols
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Access Control:</strong> AWS IAM with least-privilege
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                • <strong>Authentication:</strong> AWS Cognito secure protocols
              </Text>
            </View>

            {/* Section 6 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Data Retention
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Analysis Results:</strong> Auto-deleted after 30 days
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Uploaded CVs:</strong> Until you delete them
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                • <strong>Account Data:</strong> Until account closure
              </Text>
            </View>

            {/* Section 7 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Third-Party Services
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>OpenAI:</strong> CV analysis processing
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Reed API:</strong> Job listings (no personal data shared)
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                • <strong>AWS:</strong> Infrastructure (EU data centres)
              </Text>
            </View>

            {/* Section 8 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Your Rights (GDPR)
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Access:</strong> Request your data copy
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Rectification:</strong> Correct inaccuracies
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Erasure:</strong> Right to be forgotten
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • <strong>Portability:</strong> Export your data
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                • <strong>Withdraw Consent:</strong> At any time
              </Text>
            </View>

            {/* Section 9 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Cookies
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                We use essential cookies only:
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • Authentication tokens
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                • Session management
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                No tracking or marketing cookies are used.
              </Text>
            </View>

            {/* Section 10 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Complaints
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                If you have concerns about data handling, you may lodge a complaint with:
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                <strong>Information Commissioner's Office (ICO)</strong>
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                Website: ico.org.uk | Phone: 0303 123 1113
              </Text>
            </View>

            {/* Section 11 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Updates
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                This policy may be updated to reflect changes in practices or legal requirements. 
                Significant changes will be notified via the platform.
              </Text>
            </View>

            {/* Section 12 */}
            <View>
              <Heading level={3} style={{ color: '#667eea', marginBottom: '1rem' }}>
                Data Controller
              </Heading>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                Campus2Career operates as the data controller for all personal information 
                processed through this platform under UK GDPR regulations.
              </Text>
            </View>
          </View>

          <Button
            onClick={() => navigate('/')}
            style={{
              marginTop: '3rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          >
            Back to Home
          </Button>
        </View>
      </View>
    </View>
  );
};

export default PrivacyPolicy;